<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { detectCategoryFromRoute } from 'dashboard/helper/routeCategory';
import Avatar from 'next/avatar/Avatar.vue';

const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');

const normalizedAvailability = computed(() =>
  currentUserAvailability.value === 'online' ? 'online' : 'offline'
);

const profileStatusLabel = computed(() =>
  normalizedAvailability.value === 'online' ? 'Online' : 'Offline'
);

const profileName = computed(
  () =>
    currentUser.value?.available_name ||
    currentUser.value?.name ||
    currentUser.value?.email ||
    'Profile'
);

const activeCategory = computed(() => detectCategoryFromRoute(route, ''));
const isProfileRoute = computed(() =>
  String(route.name || '').startsWith('profile_settings')
);

const canAccessRoute = routeName => {
  let resolvedRoute;
  try {
    resolvedRoute = router.resolve(accountScopedRoute(routeName));
  } catch (error) {
    return false;
  }

  return shouldShow(
    resolvedRoute?.meta?.featureFlag || null,
    resolvedRoute?.meta?.permissions || [],
    resolvedRoute?.meta?.installationTypes || []
  );
};

const items = computed(() => {
  const candidates = [
    {
      key: 'chat',
      label: 'Chat',
      icon: 'i-ph-chats-circle-bold',
      routeName: 'home',
      active: activeCategory.value === 'Conversation',
    },
    {
      key: 'engage',
      label: 'Engage',
      icon: 'i-ph-activity-bold',
      routeName: 'engage_traffic_index',
      active: activeCategory.value === 'Engage',
    },
    {
      key: 'teams',
      label: 'Teams',
      icon: 'i-ph-users-three-bold',
      routeName: 'team_management_agents',
      active: activeCategory.value === 'TeamManagement',
    },
    {
      key: 'profile',
      label: 'Profile',
      routeName: 'profile_settings_index',
      active: isProfileRoute.value,
    },
  ];

  return candidates
    .filter(item => canAccessRoute(item.routeName))
    .map(item => ({
      ...item,
      to: accountScopedRoute(item.routeName),
    }));
});

const navigate = targetRoute => {
  router.push(targetRoute).catch(() => {});
};
</script>

<template>
  <footer class="wlab-mobile-footer md:hidden" aria-label="Mobile footer menu">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      class="wlab-mobile-footer__item"
      :class="{ 'is-active': item.active }"
      :aria-label="
        item.key === 'profile' ? `Profile, ${profileStatusLabel}` : item.label
      "
      :aria-current="item.active ? 'page' : undefined"
      @click="navigate(item.to)"
    >
      <span
        v-if="item.key !== 'profile'"
        class="wlab-mobile-footer__icon"
        :class="item.icon"
      />
      <Avatar
        v-else
        :size="26"
        :name="profileName"
        :src="currentUser.avatar_url"
        :status="normalizedAvailability"
        rounded-full
        class="wlab-mobile-footer__avatar"
      />
      <span v-if="item.key !== 'profile'" class="wlab-mobile-footer__label">
        {{ item.label }}
      </span>
    </button>
  </footer>
</template>

<style scoped>
.wlab-mobile-footer {
  display: flex;
  align-items: stretch;
  gap: 6px;
  padding: 8px 8px calc(env(safe-area-inset-bottom, 0px) + 8px);
  background: #000000;
  border-top: 1px solid #000000;
  flex-shrink: 0;
}

.wlab-mobile-footer__item {
  flex: 1;
  min-width: 0;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: rgba(255, 255, 255, 0.62);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  padding: 7px 4px 6px;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
  box-shadow: inset 0 0 0 1px transparent;
}

.wlab-mobile-footer__item:active {
  transform: scale(0.98);
}

.wlab-mobile-footer__item.is-active {
  background: #ffffff26;
  box-shadow: none;
  color: #ffffff;
}

.wlab-mobile-footer__item.is-active .wlab-mobile-footer__icon,
.wlab-mobile-footer__item.is-active .wlab-mobile-footer__label {
  text-shadow: 0 0 14px rgba(255, 255, 255, 0.12);
}

.wlab-mobile-footer__icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.wlab-mobile-footer__label {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.wlab-mobile-footer__avatar {
  flex-shrink: 0;
}

</style>
